import React, { useState, useEffect } from 'react';

import type { TournamentJoinPaymentProps } from './types';
import { getJoinPaymentTranslations } from './i18n';

const MOBILE_BREAKPOINT = 720;

const isWindowMobile = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.innerWidth < MOBILE_BREAKPOINT;
};

/**
 * TournamentJoinPayment component
 * Displays Pay & Join button for joining paid tournaments.
 * Task 12.1: Basic structure with TypeScript interfaces.
 * Task 23.3: Responsive layout for mobile devices.
 */
const TournamentJoinPayment: React.FC<TournamentJoinPaymentProps> = ({
  stake,
  currency,
  isLoading = false,
  onPayAndJoin,
  error,
  buttonLabel,
  locale = 'en',
}) => {
  const [isMobile, setIsMobile] = useState(isWindowMobile);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleClick = () => {
    if (!isLoading) {
      onPayAndJoin();
    }
  };

  const t = getJoinPaymentTranslations(locale);
  const defaultLabel = `${t.defaultLabel} ${stake} ${currency}`;
  const label = buttonLabel ? `${buttonLabel} ${stake} ${currency}` : defaultLabel;

  const layoutType = isMobile ? 'mobile' : 'desktop';

  return (
    <div data-testid="tournament-join-payment" data-layout={layoutType}>
      {error && (
        <div 
          role="alert" 
          aria-live="assertive" 
          tabIndex={-1}
          data-testid="error-message"
        >
          {error}
        </div>
      )}
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        aria-busy={isLoading ? 'true' : undefined}
        aria-disabled={isLoading}
        data-fullwidth={isMobile ? 'true' : undefined}
      >
        {isLoading && <span data-testid="loading-spinner" aria-hidden="true">⏳</span>}
        {isLoading ? ' ' : ''}{label}
      </button>
    </div>
  );
};

export default TournamentJoinPayment;
